import React, { useEffect, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import { DashboardCounts } from 'pages/documents/types'

export type Bucket = { label: string; title: string; counts: DashboardCounts }

/** Outcomes stacked bottom-up; colours are the status scale, never series hues. */
export const OUTCOMES: {
  key: keyof Omit<DashboardCounts, 'received'>
  label: string
  tone: string
}[] = [
  { key: 'completed', label: 'Completed', tone: 'good' },
  { key: 'needsReview', label: 'Needs review', tone: 'warning' },
  { key: 'failed', label: 'Failed', tone: 'critical' },
  { key: 'processing', label: 'Processing', tone: 'neutral' }
]

type Props = { buckets: Bucket[]; title: string }

const HEIGHT = 220
const PAD = { top: 12, right: 8, bottom: 28, left: 36 }
const GAP = 2

/** A bar with its free (top) end rounded, anchored square to the baseline. */
const roundedTop = (x: number, y: number, w: number, h: number, r: number) => {
  const rr = Math.min(r, h)
  return [
    `M${x},${y + h}`,
    `V${y + rr}`,
    `Q${x},${y} ${x + rr},${y}`,
    `H${x + w - rr}`,
    `Q${x + w},${y} ${x + w},${y + rr}`,
    `V${y + h}`,
    'Z'
  ].join(' ')
}

const niceMax = (value: number) => {
  if (value <= 4) return 4
  const step = 10 ** Math.floor(Math.log10(value))
  const nice = [1, 2, 2.5, 5, 10].map((m) => m * step).find((n) => n >= value)
  return nice ?? value
}

/**
 * Documents per period, stacked by outcome. Hovering (or focusing) a bar shows
 * its numbers; a table view carries the same data for screen readers and for
 * the low-contrast warning colour.
 */
const VolumeChart: React.FC<Props> = ({ buckets, title }) => {
  const [hover, setHover] = useState<number>()
  const [asTable, setAsTable] = useState(false)
  const [width, setWidth] = useState(800)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const node = ref.current
    if (!node) return undefined
    const observer = new ResizeObserver(([entry]) =>
      setWidth(Math.max(320, entry.contentRect.width))
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [asTable])

  const max = useMemo(
    () => niceMax(Math.max(0, ...buckets.map((b) => b.counts.received))),
    [buckets]
  )
  const plotW = width - PAD.left - PAD.right
  const plotH = HEIGHT - PAD.top - PAD.bottom
  const slot = plotW / Math.max(buckets.length, 1)
  const barW = Math.max(3, Math.min(28, slot * 0.62))
  const y = (v: number) => (v / max) * plotH
  const ticks = [0, max / 2, max]
  // Label every nth bucket so dates never collide.
  const labelEvery = Math.max(
    1,
    Math.ceil(buckets.length / Math.floor(plotW / 64))
  )

  const active = hover != null ? buckets[hover] : undefined
  const tipLeft =
    hover != null
      ? Math.min(Math.max(PAD.left + slot * (hover + 0.5), 90), width - 90)
      : 0

  return (
    <div className="db-chart">
      <div className="db-chart__head">
        <ul className="db-legend" aria-label="Legend">
          {OUTCOMES.map((o) => (
            <li key={o.key}>
              <span className={`db-swatch db-swatch--${o.tone}`} aria-hidden />
              {o.label}
            </li>
          ))}
        </ul>
        <button
          type="button"
          className="db-toggle"
          onClick={() => setAsTable((v) => !v)}
          aria-pressed={asTable}
        >
          {asTable ? 'Show chart' : 'Show table'}
        </button>
      </div>

      {asTable ? (
        <div className="dx-table-wrap dx-table-wrap--inner db-chart__table">
          <table className="dx-table dx-table--compact">
            <caption className="dx-sr-only">{title}</caption>
            <thead>
              <tr>
                <th scope="col">Period</th>
                <th scope="col" className="dx-num">
                  Received
                </th>
                {OUTCOMES.map((o) => (
                  <th key={o.key} scope="col" className="dx-num">
                    {o.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {buckets
                .filter((b) => b.counts.received > 0)
                .map((b) => (
                  <tr key={b.title}>
                    <th scope="row">{b.title}</th>
                    <td className="dx-num">{b.counts.received}</td>
                    {OUTCOMES.map((o) => (
                      <td key={o.key} className="dx-num">
                        {b.counts[o.key]}
                      </td>
                    ))}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="db-chart__plot" ref={ref}>
          <svg
            width={width}
            height={HEIGHT}
            role="img"
            aria-label={`${title}. Use “Show table” for the numbers.`}
          >
            {ticks.map((t) => (
              <g key={t}>
                <line
                  className="db-grid"
                  x1={PAD.left}
                  x2={width - PAD.right}
                  y1={PAD.top + plotH - y(t)}
                  y2={PAD.top + plotH - y(t)}
                />
                <text
                  className="db-axis"
                  x={PAD.left - 8}
                  y={PAD.top + plotH - y(t)}
                  dy="0.32em"
                  textAnchor="end"
                >
                  {Math.round(t)}
                </text>
              </g>
            ))}

            {buckets.map((b, i) => {
              const x = PAD.left + slot * i + (slot - barW) / 2
              let base = PAD.top + plotH
              const segments = OUTCOMES.filter((o) => b.counts[o.key] > 0)
              return (
                // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                <g
                  key={b.title}
                  className={classNames('db-bar', {
                    'db-bar--dim': hover != null && hover !== i
                  })}
                  onMouseEnter={() => setHover(i)}
                  onMouseLeave={() => setHover(undefined)}
                >
                  {/* Hit target taller and wider than the bar. */}
                  <rect
                    className="db-hit"
                    x={PAD.left + slot * i}
                    y={PAD.top}
                    width={slot}
                    height={plotH}
                  />
                  {segments.map((o, s) => {
                    const h = y(b.counts[o.key])
                    const top = s === segments.length - 1
                    const gap = s > 0 ? GAP : 0
                    const rectY = base - h + gap
                    const rectH = Math.max(h - gap, 1)
                    base -= h
                    return top ? (
                      <path
                        key={o.key}
                        className={`db-seg db-seg--${o.tone}`}
                        d={roundedTop(
                          x,
                          rectY,
                          barW,
                          rectH,
                          Math.min(4, barW / 2)
                        )}
                      />
                    ) : (
                      <rect
                        key={o.key}
                        className={`db-seg db-seg--${o.tone}`}
                        x={x}
                        y={rectY}
                        width={barW}
                        height={rectH}
                      />
                    )
                  })}
                  {i % labelEvery === 0 && (
                    <text
                      className="db-axis"
                      x={x + barW / 2}
                      y={HEIGHT - 8}
                      textAnchor="middle"
                    >
                      {b.label}
                    </text>
                  )}
                </g>
              )
            })}
            <line
              className="db-baseline"
              x1={PAD.left}
              x2={width - PAD.right}
              y1={PAD.top + plotH}
              y2={PAD.top + plotH}
            />
          </svg>

          {active && (
            <div
              className="db-tip"
              style={{ left: tipLeft }}
              role="status"
              aria-live="polite"
            >
              <p className="db-tip__title">{active.title}</p>
              <p className="db-tip__total">{active.counts.received} received</p>
              <ul>
                {OUTCOMES.map((o) => (
                  <li key={o.key}>
                    <span className={`db-swatch db-swatch--${o.tone}`} />
                    {o.label}
                    <strong>{active.counts[o.key]}</strong>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default VolumeChart
